import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import {
  AppButton,
  AppConfetti,
  AppEmptyState,
  AppInput,
  AppModal,
  AppPillSwitch,
} from '../../components/ui';
import { notify } from '../../components/common/AppToast';
import tempsService from '../../services/tempsService';
import {
  formatDayLabel,
  formatIsoDate,
  formatMinutes,
  formatWeekLabel,
  mondayOf,
  shiftWeek,
  weekOffsetCaption,
} from '../../utils/tempsWeek';
import TempsDurationInput, { durationFieldFromMinutes, toEstimatedMinutes } from './TempsDurationInput';
import '../../styles/temps-taches.scss';

const PERSON_COLORS = ['#2f6fb5', '#a4552c', '#2e7d6b', '#5f6a2a', '#b0463f', '#4d5fa0', '#8a6420', '#3f6f8f'];
const DURATION_PRESETS = [
  { key: '15', label: '15 min', amount: '15', unit: 'min' },
  { key: '30', label: '30 min', amount: '30', unit: 'min' },
  { key: '60', label: '1 h', amount: '1', unit: 'h' },
  { key: '120', label: '2 h', amount: '2', unit: 'h' },
];
const WEEK_TABS = ['mine', 'claimable', 'team'];

const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const colorForUser = (id) => PERSON_COLORS[Math.abs(Number(id) || 0) % PERSON_COLORS.length];

const sumMinutes = (rows, key) => rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

const deltaOf = (actual, estimated) => {
  const delta = (Number(actual) || 0) - (Number(estimated) || 0);
  if (!delta) return { text: 'comme prévu', tone: 'muted' };
  const sign = delta > 0 ? '+' : '−';
  return { text: `${sign}${formatMinutes(Math.abs(delta))}`, tone: delta > 0 ? 'warn' : 'ok' };
};

const matchesQuery = (task, query) => {
  if (!query) return true;
  const hay = `${task.title || ''} ${task.clientName || ''} ${task.commandeId || ''}`.toLowerCase();
  return hay.includes(query);
};

const monthKey = (value) => {
  if (!value) return 'sans-date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'sans-date';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key) => {
  if (key === 'sans-date') return 'Sans date';
  const [year, month] = key.split('-');
  const label = new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const TaskFacts = ({ task }) => (
  <div className="tt-meta">
    <span className="tt-client">
      {task.brand ? <span className={`tt-dot tt-dot--${task.brand}`} aria-hidden /> : null}
      {task.clientName || 'Client'}
    </span>
    <span className="tt-meta__sep" aria-hidden>·</span>
    <span>{task.productLabel || task.productRef || 'Tâche'}</span>
    {task.commandeId ? (
      <>
        <span className="tt-meta__sep" aria-hidden>·</span>
        <span>#{task.commandeId}</span>
      </>
    ) : null}
  </div>
);

const TaskRow = ({ task, week, flashId, pending, action }) => {
  const late = task.weekStart && task.weekStart < week;
  return (
    <div className={`tt-row${flashId === task.id ? ' is-new' : ''}${pending ? ' is-pending' : ''}`}>
      <div className="tt-row__main">
        <div className="tt-row__title">
          <span>{task.title}</span>
          {late ? (
            <span className="tt-tag">Prévue sem. du {formatDayLabel(task.weekStart)}</span>
          ) : null}
          {flashId === task.id ? <span className="tt-tag tt-tag--new">Prise à l’instant</span> : null}
        </div>
        <TaskFacts task={task} />
      </div>
      <span className="tt-prevu">
        prévu <strong>{formatMinutes(task.estimatedMinutes)}</strong>
      </span>
      {action}
    </div>
  );
};

const TempsTachesPage = () => {
  const todayWeek = formatIsoDate(mondayOf());
  const [tab, setTab] = useState('mine');
  const [week, setWeek] = useState(todayWeek);
  const [bucket, setBucket] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [celebrate, setCelebrate] = useState(0);
  const [pending, setPending] = useState(null);
  const [durationAmount, setDurationAmount] = useState('');
  const [durationUnit, setDurationUnit] = useState('min');
  const [minutesError, setMinutesError] = useState('');
  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState({});
  const [flashId, setFlashId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const scopes = ['mine', 'claimable'];
    if (tab !== 'mine' && tab !== 'claimable') scopes.push(tab);
    try {
      const results = await Promise.all(scopes.map((scope) => tempsService.listTasks(scope, week)));
      setBucket((prev) => {
        const next = { ...prev };
        scopes.forEach((scope, index) => {
          next[scope] = Array.isArray(results[index]?.tasks) ? results[index].tasks : [];
        });
        return next;
      });
    } catch (error) {
      notify.error(error?.message || 'Impossible de charger les tâches');
    } finally {
      setLoading(false);
    }
  }, [tab, week]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!flashId) return undefined;
    const timer = window.setTimeout(() => setFlashId(null), 2600);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  const mine = useMemo(() => bucket.mine || [], [bucket]);
  const claimable = useMemo(() => bucket.claimable || [], [bucket]);
  const current = useMemo(() => bucket[tab] || [], [bucket, tab]);

  const late = useMemo(
    () => mine.filter((task) => task.status === 'a_faire' && task.weekStart && task.weekStart < week)
      .sort((a, b) => String(a.weekStart).localeCompare(String(b.weekStart))),
    [mine, week],
  );
  const weekTodo = useMemo(
    () => mine.filter((task) => task.status === 'a_faire' && task.weekStart === week)
      .sort((a, b) => (b.id === flashId) - (a.id === flashId)),
    [mine, week, flashId],
  );
  const weekDone = useMemo(
    () => mine.filter((task) => task.status === 'validee' && task.weekStart === week),
    [mine, week],
  );

  const mineCount = late.length + weekTodo.length;
  const plannedCount = weekTodo.length + weekDone.length;
  const plannedMinutes = sumMinutes([...weekTodo, ...weekDone], 'estimatedMinutes');
  const passedMinutes = sumMinutes(weekDone, 'actualMinutes');
  const progress = plannedCount ? Math.round((weekDone.length / plannedCount) * 100) : 0;

  const tabs = [
    {
      value: 'mine',
      label: 'Ma semaine',
      count: mineCount || undefined,
      countTone: late.length ? 'warn' : undefined,
    },
    {
      value: 'claimable',
      label: 'À prendre',
      count: claimable.length || undefined,
    },
    { value: 'team', label: 'Équipe' },
    { value: 'validated', label: 'Validées' },
    { value: 'archived', label: 'Archives' },
  ];

  const needle = query.trim().toLowerCase();
  const histGroups = useMemo(() => {
    if (tab !== 'validated' && tab !== 'archived') return [];
    const rows = current.filter((task) => matchesQuery(task, needle));
    if (tab === 'validated') {
      const weeks = [...new Set(rows.map((task) => task.weekStart || ''))].sort((a, b) => b.localeCompare(a));
      return weeks.map((itemWeek) => {
        const groupRows = rows.filter((task) => (task.weekStart || '') === itemWeek);
        const label = itemWeek
          ? `${itemWeek === todayWeek ? 'Cette semaine · ' : ''}Semaine du ${formatWeekLabel(itemWeek)}`
          : 'Sans semaine';
        return {
          key: itemWeek || 'none',
          label,
          meta: `${plural(groupRows.length, 'tâche')} · ${formatMinutes(sumMinutes(groupRows, 'actualMinutes'))} passées`,
          rows: groupRows,
        };
      });
    }
    const keys = [...new Set(rows.map((task) => monthKey(task.archivedAt || task.validatedAt)))]
      .sort((a, b) => b.localeCompare(a));
    return keys.map((key) => {
      const groupRows = rows.filter((task) => monthKey(task.archivedAt || task.validatedAt) === key);
      return {
        key,
        label: monthLabel(key),
        meta: `${plural(groupRows.length, 'tâche')} · ${formatMinutes(sumMinutes(groupRows, 'actualMinutes'))}`,
        rows: groupRows,
      };
    });
  }, [tab, current, needle, todayWeek]);

  const teamCards = useMemo(() => {
    if (tab !== 'team') return [];
    const groups = new Map();
    current.forEach((task) => {
      const user = task.assignee;
      if (!user?.id) return;
      if (!groups.has(user.id)) {
        groups.set(user.id, { user, tasks: [] });
      }
      groups.get(user.id).tasks.push(task);
    });
    return [...groups.values()].sort((a, b) => String(a.user.name).localeCompare(String(b.user.name), 'fr'));
  }, [tab, current]);

  const spentMinutes = toEstimatedMinutes(durationAmount, durationUnit);

  const applyDuration = (amount, unit) => {
    setDurationAmount(amount);
    setDurationUnit(unit);
    setMinutesError('');
  };

  const openValidate = (task) => {
    setPending(task);
    setDurationAmount('');
    setDurationUnit('min');
    setMinutesError('');
  };

  const submitValidate = async () => {
    if (!pending || saving) return;
    const value = spentMinutes;
    if (!Number.isInteger(value) || value < 1 || value > 960) {
      setMinutesError(!Number.isInteger(value) || value < 1
        ? 'Indiquez le temps passé en minutes.'
        : 'Temps invalide : entre 1 et 960 minutes.');
      return;
    }
    const title = pending.title;
    const wasLate = pending.weekStart && pending.weekStart < week;
    const lateLeft = late.filter((task) => task.id !== pending.id).length;
    const todoLeft = weekTodo.filter((task) => task.id !== pending.id).length;
    setSaving(true);
    try {
      await tempsService.validateTask(pending.id, value);
      setPending(null);
      setDurationAmount('');
      setDurationUnit('min');
      setCelebrate((count) => count + 1);
      if (!lateLeft && !todoLeft) {
        notify.success(`Semaine bouclée ! « ${title} » validée · ${formatMinutes(value)}`);
      } else if (wasLate && !lateLeft) {
        notify.success(`Plus aucun retard, bien joué. ${formatMinutes(value)} enregistrées.`);
      } else {
        notify.success(`« ${title} » validée · ${formatMinutes(value)}`);
      }
      await load();
    } catch (error) {
      notify.error(error?.message || 'Validation impossible');
    } finally {
      setSaving(false);
    }
  };

  const claim = async (task) => {
    if (claiming[task.id]) return;
    setClaiming((prev) => ({ ...prev, [task.id]: true }));
    try {
      await tempsService.claimTask(task.id, week);
      setFlashId(task.id);
      setQuery('');
      setTab('mine');
      notify.success(`« ${task.title} » ajoutée à votre semaine`);
    } catch (error) {
      notify.error(error?.message || 'Impossible de prendre la tâche');
    } finally {
      setClaiming((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
    }
  };

  const reopen = async (task) => {
    try {
      await tempsService.reopenTask(task.id);
      notify.success(`« ${task.title} » repassée à faire`);
      await load();
    } catch (error) {
      notify.error(error?.message || 'Impossible de repasser la tâche à faire');
    }
  };

  const showWeek = WEEK_TABS.includes(tab);
  const takeCta = `Voir ${plural(claimable.length, 'tâche')} à prendre`;

  return (
    <MasterLayout>
      <Breadcrumb title="Mes tâches" />
      {celebrate > 0 ? <AppConfetti key={celebrate} /> : null}
      <div className="tt-page">
        <div className="tt-toolbar">
          <AppPillSwitch
            variant="underline"
            options={tabs}
            value={tab}
            ariaLabel="Mes tâches"
            onChange={(next) => {
              setQuery('');
              setTab(next);
            }}
          />
          {showWeek ? (
            <div className="tt-week">
              {week !== todayWeek ? (
                <AppButton size="sm" variant="outline-secondary" onClick={() => setWeek(todayWeek)}>
                  Cette semaine
                </AppButton>
              ) : null}
              <AppButton
                size="sm"
                variant="outline-secondary"
                icon="mdi:chevron-left"
                aria-label="Semaine précédente"
                onClick={() => setWeek((value) => shiftWeek(value, -1))}
              />
              <div className="tt-week__label">
                <span>{formatWeekLabel(week)}</span>
                <small>{weekOffsetCaption(week)}</small>
              </div>
              <AppButton
                size="sm"
                variant="outline-secondary"
                icon="mdi:chevron-right"
                aria-label="Semaine suivante"
                onClick={() => setWeek((value) => shiftWeek(value, 1))}
              />
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="tt-card tt-skel" role="status" aria-label="Chargement des tâches">
            {['42%', '58%', '36%', '50%'].map((width) => (
              <div key={width} className="tt-skel__row">
                <div className="tt-skel__lines">
                  <span className="tt-skel__bar" style={{ width }} />
                  <span className="tt-skel__bar is-sub" />
                </div>
                <span className="tt-skel__btn" />
              </div>
            ))}
          </div>
        ) : null}

        {!loading && tab === 'mine' ? (
          <>
            <div className="tt-progress">
              <div className="tt-progress__label">
                {plannedCount
                  ? `${weekDone.length} sur ${plural(plannedCount, 'tâche')} validée${weekDone.length > 1 ? 's' : ''}`
                  : 'Aucune tâche prévue'}
                {plannedCount ? (
                  <span className="tt-progress__sub">
                    {formatMinutes(passedMinutes)} passées · {formatMinutes(plannedMinutes)} prévues
                  </span>
                ) : null}
              </div>
              <div className="tt-progress__track" aria-hidden>
                <div className="tt-progress__bar" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {late.length > 0 ? (
              <section className="tt-card tt-card--late" aria-label="En retard">
                <div className="tt-card__head">
                  <span className="tt-late__icon" aria-hidden>
                    <Icon icon="mdi:timer-sand" />
                  </span>
                  <div className="tt-late__copy">
                    <span className="tt-late__title">
                      {plural(late.length, 'tâche')} à rattraper · {formatMinutes(sumMinutes(late, 'estimatedMinutes'))}
                    </span>
                    <span className="tt-card__meta">Reportées des semaines précédentes. Commencez par elles pour repartir à zéro.</span>
                  </div>
                </div>
                {late.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    week={week}
                    action={(
                      <AppButton icon="mdi:check" onClick={() => openValidate(task)}>Valider</AppButton>
                    )}
                  />
                ))}
              </section>
            ) : null}

            <section className="tt-card" aria-label="Cette semaine">
              <div className="tt-card__head">
                <span className="tt-card__title">
                  {week === todayWeek ? 'Cette semaine' : `Semaine du ${formatWeekLabel(week)}`}
                </span>
                {weekTodo.length ? (
                  <span className="tt-card__meta">
                    {plural(weekTodo.length, 'tâche')} à valider · {formatMinutes(sumMinutes(weekTodo, 'estimatedMinutes'))}
                  </span>
                ) : null}
              </div>
              {weekTodo.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  week={week}
                  flashId={flashId}
                  action={(
                    <AppButton icon="mdi:check" onClick={() => openValidate(task)}>Valider</AppButton>
                  )}
                />
              ))}
              {!weekTodo.length && weekDone.length > 0 ? (
                <AppEmptyState
                  icon="mdi:party-popper"
                  message="Semaine bouclée"
                  description="Toutes vos tâches de la semaine sont validées. Il reste peut-être des tâches à prendre pour l’équipe."
                  actions={claimable.length ? [{
                    key: 'take',
                    label: takeCta,
                    variant: 'secondary',
                    onClick: () => setTab('claimable'),
                  }] : []}
                />
              ) : null}
              {!weekTodo.length && !weekDone.length ? (
                <AppEmptyState
                  icon="mdi:calendar-blank-outline"
                  message="Rien de prévu pour vous cette semaine"
                  description="Le chef de projet ne vous a encore rien assigné. Vous pouvez prendre une tâche disponible."
                  actions={claimable.length ? [{
                    key: 'take',
                    label: takeCta,
                    variant: 'primary',
                    onClick: () => setTab('claimable'),
                  }] : []}
                />
              ) : null}
              {weekDone.length > 0 ? (
                <div className="tt-done-block">
                  <div className="tt-done-block__title">Validées cette semaine · {weekDone.length}</div>
                  {weekDone.map((task) => (
                    <div key={task.id} className="tt-done">
                      <Icon icon="mdi:check-circle" className="tt-done__ok" aria-hidden />
                      <span className="tt-done__title">{task.title}</span>
                      <span className="tt-card__meta">{formatMinutes(task.actualMinutes)} passées</span>
                      <AppButton
                        variant="link"
                        size="sm"
                        icon="mdi:undo"
                        title="Repasser en à faire"
                        onClick={() => reopen(task)}
                      >
                        Repasser à faire
                      </AppButton>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          </>
        ) : null}

        {!loading && tab === 'claimable' ? (
          <section className="tt-card">
            <div className="tt-note">
              <Icon icon="mdi:hand-back-right-outline" aria-hidden />
              Tâches sans personne assignée. En prenant une tâche, elle rejoint votre semaine.
            </div>
            {claimable.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                week={week}
                pending={Boolean(claiming[task.id])}
                action={(
                  <AppButton
                    icon="mdi:plus"
                    loading={Boolean(claiming[task.id])}
                    loadingLabel="Prendre"
                    onClick={() => claim(task)}
                  >
                    Prendre
                  </AppButton>
                )}
              />
            ))}
            {!claimable.length ? (
              <AppEmptyState
                icon="mdi:inbox-outline"
                message="Aucune tâche à prendre"
                description="Toutes les tâches de cette semaine ont une personne assignée."
              />
            ) : null}
          </section>
        ) : null}

        {!loading && tab === 'team' ? (
          <>
            <div className="tt-note">
              <Icon icon="mdi:eye-outline" aria-hidden />
              Lecture seule : ce que fait le reste de l’équipe cette semaine.
            </div>
            {teamCards.length ? (
              <div className="tt-team-grid">
                {teamCards.map((card) => (
                  <section key={card.user.id} className="tt-card">
                    <div className="tt-person">
                      <span className="tt-avatar" style={{ background: colorForUser(card.user.id) }}>
                        {initials(card.user.name)}
                      </span>
                      <div>
                        <div className="tt-person__name">{card.user.name}</div>
                        <div className="tt-person__meta">
                          {plural(card.tasks.length, 'tâche')} · {formatMinutes(sumMinutes(card.tasks, 'estimatedMinutes'))} prévues
                        </div>
                      </div>
                    </div>
                    {card.tasks.map((task) => {
                      const done = task.status === 'validee';
                      return (
                        <div key={task.id} className="tt-team-row">
                          <Icon
                            icon={done ? 'mdi:check-circle' : 'mdi:circle-outline'}
                            className={`tt-team-row__icon${done ? ' is-done' : ' is-todo'}`}
                            aria-hidden
                          />
                          <div className="tt-team-row__main">
                            <span className={`tt-team-row__title${done ? ' is-done' : ''}`}>{task.title}</span>
                            <span className="tt-team-row__client">
                              {task.clientName || 'Client'}
                              {task.commandeId ? ` · #${task.commandeId}` : ''}
                            </span>
                          </div>
                          <span className="tt-team-row__status">{done ? 'Validée' : 'À faire'}</span>
                        </div>
                      );
                    })}
                  </section>
                ))}
              </div>
            ) : (
              <AppEmptyState
                icon="mdi:account-group-outline"
                message="Aucune tâche pour le reste de l’équipe"
                description="Personne d’autre n’a de tâche assignée sur cette semaine."
              />
            )}
          </>
        ) : null}

        {!loading && (tab === 'validated' || tab === 'archived') ? (
          <>
            <div className="tt-filters">
              <label className="tt-search">
                <Icon icon="mdi:magnify" aria-hidden />
                <AppInput
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label="Rechercher"
                  placeholder="Tâche, client ou n° de commande"
                />
              </label>
              <span className="tt-card__meta">
                {tab === 'validated'
                  ? 'Vos tâches validées, semaine par semaine.'
                  : 'Tâches des commandes clôturées.'}
              </span>
            </div>
            {histGroups.map((group) => (
              <section key={group.key} className="tt-card">
                <div className="tt-hist__head">
                  <span className="tt-card__title">{group.label}</span>
                  <span className="tt-card__meta">{group.meta}</span>
                </div>
                {group.rows.map((task) => {
                  const delta = deltaOf(task.actualMinutes, task.estimatedMinutes);
                  const archived = tab === 'archived';
                  return (
                    <div key={task.id} className="tt-hist__row">
                      <Icon
                        icon={archived ? 'mdi:archive-outline' : 'mdi:check-circle'}
                        className={archived ? 'tt-team-row__icon is-todo' : 'tt-done__ok'}
                        aria-hidden
                      />
                      <div className="tt-hist__main">
                        <span className="tt-hist__title">{task.title}</span>
                        <span className="tt-hist__meta">
                          {task.clientName || 'Client'}
                          {task.commandeId ? ` · #${task.commandeId}` : ''}
                          {' · '}
                          {archived
                            ? `archivée le ${formatDayLabel(task.archivedAt || task.validatedAt)}`
                            : `prévu ${formatMinutes(task.estimatedMinutes)}`}
                        </span>
                      </div>
                      <span className="tt-hist__passe">{formatMinutes(task.actualMinutes)}</span>
                      <span className={`tt-delta is-${delta.tone}`}>{delta.text}</span>
                      {!archived ? (
                        <AppButton
                          variant="link"
                          size="sm"
                          icon="mdi:undo"
                          title="Repasser en à faire"
                          onClick={() => reopen(task)}
                        >
                          Repasser à faire
                        </AppButton>
                      ) : null}
                    </div>
                  );
                })}
              </section>
            ))}
            {!histGroups.length ? (
              <AppEmptyState
                icon={needle ? 'mdi:magnify-close' : 'mdi:clipboard-text-outline'}
                message={needle
                  ? `Aucun résultat pour « ${query.trim()} »`
                  : (tab === 'validated' ? 'Aucune tâche validée pour l’instant' : 'Aucune archive')}
              />
            ) : null}
          </>
        ) : null}
      </div>

      <AppModal
        show={Boolean(pending)}
        labelledBy="tt-validate-title"
        centered
        dialogClassName="tt-validate-dialog"
        onClose={() => { if (!saving) setPending(null); }}
        closeDisabled={saving}
        header={(
          <div className="modal-header tt-validate__head">
            <div className="tt-validate__intro">
              <span className="tt-validate__kicker">Valider la tâche</span>
              <p id="tt-validate-title" className="tt-validate__title">{pending?.title}</p>
              <p className="tt-validate__meta">
                {pending?.clientName || 'Client'}
                {pending?.commandeId ? ` · #${pending.commandeId}` : ''}
                {' · prévu '}
                {formatMinutes(pending?.estimatedMinutes)}
              </p>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Fermer"
              onClick={() => { if (!saving) setPending(null); }}
              disabled={saving}
            />
          </div>
        )}
        footer={(
          <>
            <AppButton variant="link" onClick={() => setPending(null)} disabled={saving}>Annuler</AppButton>
            <AppButton icon="mdi:check" loading={saving} loadingLabel="Validation…" onClick={submitValidate}>Valider</AppButton>
          </>
        )}
      >
        <div className="tt-validate__label">
          <span>Temps passé <abbr className="tt-validate__req" title="obligatoire">*</abbr></span>
          <span>{formatMinutes(Number.isFinite(spentMinutes) ? spentMinutes : 0)}</span>
        </div>
        <TempsDurationInput
          className="tt-validate__duration"
          value={durationAmount}
          unit={durationUnit}
          invalid={Boolean(minutesError)}
          ariaLabel="Temps passé"
          onChange={({ amount, unit }) => {
            setDurationAmount(amount);
            setDurationUnit(unit);
            setMinutesError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submitValidate();
          }}
        />
        {minutesError ? <p className="tt-validate__error">{minutesError}</p> : null}
        <div className="tt-presets">
          <AppButton
            variant="outline-secondary"
            size="sm"
            className="tt-presets__chip"
            onClick={() => {
              const next = durationFieldFromMinutes(pending?.estimatedMinutes || 0);
              applyDuration(next.amount, next.unit);
            }}
          >
            Comme prévu · {pending?.estimatedMinutes || 0} min
          </AppButton>
          {DURATION_PRESETS.map((preset) => (
            <AppButton
              key={preset.key}
              variant="outline-secondary"
              size="sm"
              className="tt-presets__chip"
              onClick={() => applyDuration(preset.amount, preset.unit)}
            >
              {preset.label}
            </AppButton>
          ))}
        </div>
      </AppModal>
    </MasterLayout>
  );
};

export default TempsTachesPage;
